import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';

import type {
  GenerationConnector,
  GenerationConnectorRepair,
  GenerationConnectorTestState,
} from '../lib/api.js';
import { api, ApiError } from '../lib/api.js';
import { displayValue } from '../lib/catalogueDiff.js';
import {
  attemptsHint,
  clampAutoAttempts,
  MAX_AUTO_ATTEMPTS,
  MAX_AUTO_ATTEMPTS_VIDEO,
  patchRows,
  patchToUpdateInput,
  type RepairAttempt,
} from '../lib/connectorRepair.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import type { TranslationKey } from '../i18n/translations.js';
import { ChangeRow } from './ConnectorChangeRow.js';

/**
 * The self-correcting loop for a connector whose test failed:
 *
 *   ask a text model what is wrong -> show the proposal as a before/after ->
 *   the person applies it -> it is saved and the test runs again -> if it still
 *   fails, ask again, telling the model what was already tried.
 *
 * Nothing is applied without a press. The "apply automatically" box is the
 * person's go-ahead for THIS run only (it starts unticked every time), capped
 * at three rounds, and at two for video, where every retest is a billed call.
 */

interface Props {
  /** The saved connector, as last listed. */
  connector: GenerationConnector;
  /** Only offered after a failed test; a run in progress or finished stays visible regardless. */
  failed: boolean;
  /** The prompt and reference the person's own tests use, so the retest is the same test. */
  testInput: { prompt?: string; referenceMediaIds?: string[] };
  /** Called with the saved connector after each applied change, so a form showing it can follow. */
  onSaved?: (connector: GenerationConnector) => void;
  /** Milliseconds between looks at a running test. */
  pollMs?: number;
}

type Phase = 'idle' | 'asking' | 'proposal' | 'applying' | 'retesting' | 'finished';

interface Outcome {
  kind: 'passed' | 'stopped' | 'failed' | 'nothing';
  rounds: number;
  error?: string | null;
}

/** An attempt as shown in the history: what was proposed, and how the retest went. */
interface ShownAttempt extends RepairAttempt {
  rows: ReturnType<typeof patchRows>;
  model: string;
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export function ConnectorRepairAssistant({ connector, failed, testInput, onSaved, pollMs = 3000 }: Props) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const uid = useId();
  const isVideo = connector.capability === 'VIDEO';
  const maxRounds = isVideo ? MAX_AUTO_ATTEMPTS_VIDEO : MAX_AUTO_ATTEMPTS;

  const [phase, setPhase] = useState<Phase>('idle');
  const [note, setNote] = useState('');
  const [auto, setAuto] = useState(false);
  const [autoMax, setAutoMax] = useState(Math.min(2, maxRounds));
  const [round, setRound] = useState(0);
  const [proposal, setProposal] = useState<{ result: GenerationConnectorRepair; before: GenerationConnector } | null>(null);
  const [history, setHistory] = useState<ShownAttempt[]>([]);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [repairModel, setRepairModel] = useState<string | null>(null);

  // What was tried, for the hint. A ref as well as state: the loop reads it between awaits.
  const attempts = useRef<RepairAttempt[]>([]);
  const latest = useRef(connector);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    if (phase === 'idle' || phase === 'finished' || phase === 'proposal') latest.current = connector;
  }, [connector, phase]);

  const message = (err: unknown) => (err instanceof ApiError ? err.message : t('common.requestFailed'));

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['generation-connectors'] });
    void queryClient.invalidateQueries({ queryKey: ['generation-connector-test', connector.id] });
  };

  /** Look at the test until it is over and its verdict is on the connector. */
  const waitForVerdict = async (): Promise<GenerationConnectorTestState> => {
    let state = await api.getGenerationConnectorTest(connector.id);
    let settling = 0;
    while (alive.current) {
      const over = state.test !== null && !state.test.running;
      if (over && state.connector.verificationStatus !== 'UNTESTED') break;
      // The verdict is written just after the job ends; give it a few looks, then trust the job.
      if (over && (settling += 1) > 5) break;
      await sleep(pollMs);
      state = await api.getGenerationConnectorTest(connector.id);
    }
    return state;
  };

  /** Save the proposal and run the test again. Returns null when the person left. */
  const applyAndRetest = async (
    result: GenerationConnectorRepair,
    before: GenerationConnector,
  ): Promise<{ passed: boolean; error: string | null } | null> => {
    setPhase('applying');
    const saved = await api.updateGenerationConnector(
      connector.id,
      patchToUpdateInput(result.patch) as Parameters<typeof api.updateGenerationConnector>[1],
    );
    latest.current = saved;
    onSaved?.(saved);
    if (!alive.current) return null;

    setPhase('retesting');
    await api.testGenerationConnector(connector.id, testInput);
    const state = await waitForVerdict();
    if (!alive.current) return null;
    refresh();

    const passed =
      state.connector.verificationStatus === 'PASSED' ||
      (state.connector.verificationStatus === 'UNTESTED' && state.test?.status === 'DONE');
    const failure = passed ? null : (state.connector.lastTestError ?? state.test?.errorMessage ?? '');
    const attempt: RepairAttempt = { explanation: result.explanation, patch: result.patch, error: failure };
    attempts.current = [...attempts.current, attempt];
    setHistory((list) => [...list, { ...attempt, rows: patchRows(before, result.patch), model: result.model }]);
    return { passed, error: failure };
  };

  /** One run: ask, and either wait for the press or, when the person asked for it, apply and go round again. */
  const run = async () => {
    if (isVideo && !window.confirm(t('studioConnectors.repair.videoConfirm'))) return;
    const automatic = auto;
    const rounds = automatic ? clampAutoAttempts(autoMax, connector.capability) : 1;
    setError(null);
    setOutcome(null);
    setProposal(null);

    try {
      for (let current = 1; current <= rounds; current += 1) {
        setRound(current);
        setPhase('asking');
        const result = await api.repairGenerationConnector(connector.id, {
          hint: attemptsHint(note, attempts.current) || undefined,
        });
        if (!alive.current) return;
        setRepairModel(result.model);

        if (Object.keys(result.patch).length === 0) {
          setProposal({ result, before: latest.current });
          setOutcome({ kind: 'nothing', rounds: current });
          setPhase('finished');
          return;
        }
        if (!automatic) {
          setProposal({ result, before: latest.current });
          setPhase('proposal');
          return;
        }

        const verdict = await applyAndRetest(result, latest.current);
        if (!verdict || !alive.current) return;
        if (verdict.passed) {
          setOutcome({ kind: 'passed', rounds: current });
          setPhase('finished');
          return;
        }
        if (current === rounds) {
          setOutcome({ kind: 'stopped', rounds: current, error: verdict.error });
          setPhase('finished');
          return;
        }
      }
    } catch (err) {
      if (!alive.current) return;
      setError(message(err));
      setPhase('idle');
    }
  };

  /** The person pressed "apply and retest" on a shown proposal. */
  const applyPressed = async () => {
    if (!proposal) return;
    const { result, before } = proposal;
    setProposal(null);
    setError(null);
    try {
      const verdict = await applyAndRetest(result, before);
      if (!verdict || !alive.current) return;
      setOutcome(
        verdict.passed
          ? { kind: 'passed', rounds: attempts.current.length }
          : { kind: 'failed', rounds: attempts.current.length, error: verdict.error },
      );
      setPhase('finished');
    } catch (err) {
      if (!alive.current) return;
      setError(message(err));
      setPhase('idle');
    }
  };

  const cancel = () => {
    setProposal(null);
    setPhase(history.length > 0 ? 'finished' : 'idle');
  };

  const busy = phase === 'asking' || phase === 'applying' || phase === 'retesting';
  const visible = failed || phase !== 'idle' || history.length > 0;
  if (!visible) return null;

  const rows = proposal ? patchRows(proposal.before, proposal.result.patch) : [];
  const fieldLabel = (field: string) => t(`studioConnectors.field.${field}` as TranslationKey);

  return (
    <section className="repair-assistant" aria-label={t('studioConnectors.repair.title')}>
      <h4>{t('studioConnectors.repair.title')}</h4>
      <p className="muted small">{t('studioConnectors.repair.hint')}</p>
      <p className="small">
        {t('studioConnectors.repair.target', { name: connector.name })}
        {repairModel && (
          <>
            {' · '}
            {t('studioConnectors.repair.usedModel', { name: repairModel })}
          </>
        )}
      </p>
      {isVideo && <p className="warn-text small">{t('studioConnectors.repair.videoWarn')}</p>}

      {phase !== 'proposal' && !busy && (
        <>
          <label htmlFor={`repair-note-${uid}`}>{t('studioConnectors.repair.note')}</label>
          <textarea
            id={`repair-note-${uid}`}
            dir="auto"
            rows={2}
            maxLength={1000}
            placeholder={t('studioConnectors.repair.notePlaceholder')}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
          <div className="repair-auto">
            <label className="check-row">
              <input type="checkbox" checked={auto} onChange={(event) => setAuto(event.target.checked)} />
              <span className="tiny">{t('studioConnectors.repair.auto')}</span>
            </label>
            <select
              aria-label={t('studioConnectors.repair.autoCount')}
              value={autoMax}
              disabled={!auto}
              onChange={(event) => setAutoMax(Number(event.target.value))}
            >
              {Array.from({ length: maxRounds }, (_, index) => index + 1).map((count) => (
                <option key={count} value={count}>
                  {count}
                </option>
              ))}
            </select>
            <span className="tiny">{t('studioConnectors.repair.times')}</span>
          </div>
          <div className="actions">
            <button type="button" className="primary" onClick={() => void run()}>
              {phase === 'finished' ? t('studioConnectors.repair.again') : t('studioConnectors.repair.run')}
            </button>
          </div>
        </>
      )}

      {busy && (
        <p className="muted small" role="status" aria-live="polite">
          {auto && round > 0 && <>{t('studioConnectors.repair.round', { n: round, max: clampAutoAttempts(autoMax, connector.capability) })} · </>}
          {phase === 'asking' && t('studioConnectors.repair.asking')}
          {phase === 'applying' && t('studioConnectors.repair.applying')}
          {phase === 'retesting' && t('studioConnectors.repair.retesting')}
        </p>
      )}
      {error && <p className="error small">{error}</p>}

      {proposal && (
        <div className="repair-proposal">
          <p className="small" dir="auto">
            <strong>{t('studioConnectors.repair.wrong')}</strong> {proposal.result.explanation}
          </p>
          <p className="tiny muted">
            {t('studioConnectors.repair.confidence', {
              level: t(`studioConnectors.repair.confidence.${proposal.result.confidence}` as TranslationKey),
            })}
            {' · '}
            {t('studioConnectors.repair.usedModel', { name: proposal.result.model })}
          </p>

          {rows.length > 0 ? (
            <>
              <h5>{t('studioConnectors.repair.changes')}</h5>
              <ul className="change-list">
                {rows.map((row) => (
                  <ChangeRow
                    key={row.field}
                    label={fieldLabel(row.field)}
                    before={displayValue(row.before)}
                    after={displayValue(row.after)}
                  />
                ))}
              </ul>
            </>
          ) : (
            <p className="warn-text small">{t('studioConnectors.repair.noChanges')}</p>
          )}

          {proposal.result.dropped.length > 0 && (
            <>
              <h5>{t('studioConnectors.repair.dropped')}</h5>
              <ul className="repair-dropped tiny">
                {proposal.result.dropped.map((entry) => (
                  <li key={entry.field}>
                    <code className="ltr mono">{entry.field}</code> — <span dir="auto">{entry.reason}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          <div className="actions">
            {rows.length > 0 && (
              <button type="button" className="primary" onClick={() => void applyPressed()}>
                {t('studioConnectors.repair.apply')}
              </button>
            )}
            <button type="button" className="ghost" onClick={cancel}>
              {t('automation.agentCancel')}
            </button>
          </div>
        </div>
      )}

      {outcome && outcome.kind !== 'nothing' && (
        <p
          className={outcome.kind === 'passed' ? 'ok-text small' : 'error small'}
          role="status"
          dir="auto"
        >
          {outcome.kind === 'passed' && t('studioConnectors.repair.passed', { n: attempts.current.length })}
          {outcome.kind === 'stopped' &&
            t('studioConnectors.repair.stopped', { n: outcome.rounds, error: outcome.error ?? '' })}
          {outcome.kind === 'failed' && t('studioConnectors.repair.failedAgain', { error: outcome.error ?? '' })}
        </p>
      )}
      {history.length > 0 && (
        <details className="repair-history">
          <summary>{t('studioConnectors.repair.history', { n: history.length })}</summary>
          <ol>
            {history.map((attempt, index) => (
              <li key={index}>
                <p className="small" dir="auto">
                  {attempt.explanation}
                </p>
                <ul className="tiny">
                  {attempt.rows.map((row) => (
                    <li key={row.field}>
                      <code className="ltr mono">{row.field}</code>
                    </li>
                  ))}
                </ul>
                <p className={attempt.error === null ? 'ok-text tiny' : 'muted tiny'} dir="auto">
                  {attempt.error === null
                    ? t('studioConnectors.repair.attemptPassed')
                    : t('studioConnectors.repair.attemptFailed', { error: attempt.error })}
                </p>
              </li>
            ))}
          </ol>
        </details>
      )}
    </section>
  );
}
